/**
 * RolesTab — Karten-Grid aller Berechtigungsrollen.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Shield, Plus, Trash2, Eye, Globe } from 'lucide-react';
import { SCOPE_TYPES } from './constants';

const countLevels = (perms) => {
  const counts = { edit: 0, execute: 0, view: 0, none: 0 };
  if (!perms) return counts;
  Object.values(perms).forEach((v) => { if (counts[v] !== undefined) counts[v]++; });
  return counts;
};

export const RolesTab = ({ roles, onEdit, onCleanupDuplicates, onPreview }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <div>
        <CardTitle>Berechtigungsrollen</CardTitle>
        <CardDescription>Definieren Sie Rollen mit abgestuften Berechtigungen (Ansehen / Ausfuehren / Bearbeiten)</CardDescription>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={onCleanupDuplicates} data-testid="btn-cleanup-duplicate-roles">
          <Trash2 className="h-4 w-4 mr-2" /> Duplikate bereinigen
        </Button>
        <Button onClick={() => onEdit(null)} data-testid="btn-new-role">
          <Plus className="h-4 w-4 mr-2" /> Neue Rolle
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => {
          const scopeType = SCOPE_TYPES.find((s) => s.value === role.scope_type);
          const ScopeIcon = scopeType?.icon || Globe;
          const counts = countLevels(role.permissions);
          return (
            <Card
              key={role.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onEdit(role)}
              data-testid={`role-card-${role.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${role.color}20` }}
                    >
                      <Shield className="h-5 w-5" style={{ color: role.color }} />
                    </div>
                    <div>
                      <p className="font-medium">{role.name}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <ScopeIcon className="h-3 w-3" /> {scopeType?.label}
                      </div>
                    </div>
                  </div>
                  {role.is_system && <Badge variant="outline" className="text-xs">System</Badge>}
                </div>
                {role.description && (
                  <p className="text-sm text-muted-foreground mt-3">{role.description}</p>
                )}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="flex gap-1">
                    {counts.edit > 0 && (
                      <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                        {counts.edit}x Bearbeiten
                      </Badge>
                    )}
                    {counts.execute > 0 && (
                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                        {counts.execute}x Ausfuehren
                      </Badge>
                    )}
                    {counts.view > 0 && (
                      <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                        {counts.view}x Ansehen
                      </Badge>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{role.user_count} Benutzer</span>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 text-xs"
                    data-testid={`btn-preview-${role.id}`}
                    onClick={(e) => { e.stopPropagation(); onPreview(role); }}
                  >
                    <Eye className="h-3.5 w-3.5" />
                    Vorschau starten
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </CardContent>
  </Card>
);
